import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Sparkles, Plus, Download, PlayCircle } from "lucide-react";
import { api, errMsg, pollJob, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CELL = {
  completed: ["Completed", "bg-health text-navy"], expiring: ["Expiring", "bg-[#FFA000] text-navy"], expired: ["Expired", "bg-alert text-white"],
  overdue: ["Overdue", "bg-alert/80 text-white"], in_progress: ["In progress", "bg-sky-500/20 text-sky-700 dark:text-sky-300"], not_started: ["Not started", "bg-slate-500/15 text-slate-600 dark:text-slate-300"],
  required: ["Required", "border border-dashed border-alert/50 text-alert"], not_required: ["—", "text-muted-foreground"],
};
const Pill = ({ s }) => <span className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-medium ${CELL[s]?.[1]}`}>{CELL[s]?.[0] || s}</span>;
const statusOf = (a) => {
  const t = new Date().toISOString().slice(0, 10);
  if (a.status === "completed") return a.expires_at < t ? "expired" : "completed";
  if (a.due_date < t) return "overdue";
  return a.status === "in_progress" ? "in_progress" : "not_started";
};

const MyTraining = () => {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/training/my").then((r) => setList(r.data)).catch((e) => { toast.error(errMsg(e)); setList([]); });
  }, []);
  if (!list) return <Loader />;
  if (!list.length) return <EmptyState icon={GraduationCap} title="No training assigned" text="Training your manager assigns to you will appear here." />;
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{list.map((a) => (
      <div key={a.id} className="card-lift rounded-2xl border bg-card p-5" data-testid={`my-training-${a.id}`}>
        <div className="flex items-start justify-between gap-2"><h3 className="font-semibold">{a.course_title}</h3><Pill s={statusOf(a)} /></div>
        <p className="mt-1 text-xs text-muted-foreground">{a.refresher ? "Refresher · " : ""}Due {a.due_date}{a.score != null && ` · Last score ${a.score}%`}</p>
        {a.status === "completed" ? (
          <div className="mt-4 flex items-center justify-between"><span className="text-xs text-muted-foreground">Valid until {a.expires_at}</span>
            <Button size="sm" variant="outline" onClick={() => download(`/training/assignments/${a.id}/certificate`, "training-record.pdf")} data-testid={`certificate-${a.id}`}><Download className="mr-1 h-3.5 w-3.5" />Record</Button></div>
        ) : <Button asChild size="sm" className="mt-4 w-full"><Link to={`/app/training/take/${a.id}`} data-testid={`start-training-${a.id}`}><PlayCircle className="mr-1.5 h-4 w-4" />{a.status === "in_progress" ? "Continue" : "Start"}</Link></Button>}
      </div>
    ))}</div>
  );
};

const AiCourse = ({ open, setOpen }) => {
  const nav = useNavigate();
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/training/courses/ai-draft", { prompt: p, client_request_id: crypto.randomUUID() });
      const job = await pollJob(data.job_id);
      toast.success("Course draft created — review before publishing");
      nav(`/app/training/courses/${job.course_id}`);
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
      <DialogContent><DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-health" />AI course builder</DialogTitle></DialogHeader>
        <Textarea rows={5} value={p} onChange={(e) => setP(e.target.value)} disabled={busy} placeholder="Create a 20-minute asbestos awareness briefing for maintenance operatives working in residential properties." data-testid="ai-course-prompt" />
        <p className="text-xs text-muted-foreground">Produces objectives, sections, key messages and a quiz. 2 credits, charged only on success. A competent person must review before publishing.</p>
        <Button onClick={run} disabled={busy || p.length < 5} data-testid="ai-course-submit">{busy ? "Drafting course (about a minute)…" : "Draft course"}</Button>
      </DialogContent>
    </Dialog>
  );
};

const Courses = () => {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [ai, setAi] = useState(false);
  useEffect(() => {
    api.get("/training/courses").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);
  const blank = async () => {
    const { data } = await api.post("/training/courses", { title: "New course", sections: [{ heading: "Introduction", content: "" }], quiz: [] });
    nav(`/app/training/courses/${data.course_id}`);
  };
  if (!list) return <Loader />;
  return (
    <div className="space-y-4">
      <div className="flex gap-2"><Button onClick={() => setAi(true)} data-testid="ai-course-open"><Sparkles className="mr-1.5 h-4 w-4" />Draft with AI</Button><Button variant="outline" onClick={blank} data-testid="blank-course"><Plus className="mr-1.5 h-4 w-4" />Blank course</Button></div>
      {list.length === 0 ? <EmptyState icon={GraduationCap} title="No courses yet" text="Draft your first course with AI in under a minute." /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{list.map((c) => (
          <Link key={c.id} to={`/app/training/courses/${c.id}`} className="card-lift rounded-2xl border bg-card p-5" data-testid={`course-card-${c.id}`}>
            <div className="flex justify-between gap-2"><h3 className="font-semibold">{c.title}</h3><span className={`h-fit rounded-full px-2 py-0.5 text-[11px] ${c.status === "published" ? "bg-health/15 text-health" : "bg-muted text-muted-foreground"}`}>{c.status}</span></div>
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.description}</p>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">{c.duration_minutes} min · pass {c.pass_mark}% · refresher {c.validity_months}m{c.ai_generated && " · AI draft"}</p>
          </Link>
        ))}</div>
      )}
      <AiCourse open={ai} setOpen={setAi} />
    </div>
  );
};

const Matrix = () => {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/training/matrix").then((r) => setM(r.data)).catch(() => setM({ courses: [], rows: [], totals: {} }));
  }, []);
  if (!m) return <Loader />;
  if (!m.courses.length) return <EmptyState icon={GraduationCap} title="No published courses" text="Publish a course to see the training matrix." />;
  return (
    <div className="space-y-4" data-testid="training-matrix">
      <div className="flex flex-wrap gap-2">{Object.entries(m.totals).filter(([k]) => k !== "not_required").map(([k, v]) => <span key={k} className="flex items-center gap-1.5 text-xs"><Pill s={k} />{v}</span>)}</div>
      <div className="overflow-x-auto rounded-2xl border bg-card"><table className="w-full text-sm"><thead className="border-b text-left text-xs text-muted-foreground"><tr><th className="sticky left-0 bg-card p-3">Employee</th>{m.courses.map((c) => <th key={c.id} className="min-w-[130px] p-3 font-medium">{c.title}</th>)}</tr></thead>
        <tbody>{m.rows.map((r) => <tr key={r.id} className="border-b last:border-0"><td className="sticky left-0 bg-card p-3"><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.role?.replace("_", " ")}</p></td>{m.courses.map((c) => <td key={c.id} className="p-3"><Pill s={r.cells[c.id]} /></td>)}</tr>)}</tbody></table></div>
    </div>
  );
};

export default function Training() {
  const { user } = useAuth();
  const manager = ["owner", "admin", "hse_manager", "trainer"].includes(user.role);
  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="training-page">
      <div><p className="eyebrow">Training & competency</p><h1 className="mt-2 text-3xl font-bold">Training</h1>
        <p className="text-sm text-muted-foreground">Internal course completions are not externally accredited qualifications and do not replace legally required training.</p></div>
      <Tabs defaultValue={manager ? "courses" : "my"}>
        <TabsList><TabsTrigger value="my" data-testid="tab-my-training">My training</TabsTrigger>{manager && <><TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger><TabsTrigger value="matrix" data-testid="tab-matrix">Training matrix</TabsTrigger></>}</TabsList>
        <TabsContent value="my" className="mt-6"><MyTraining /></TabsContent>
        {manager && <TabsContent value="courses" className="mt-6"><Courses /></TabsContent>}
        {manager && <TabsContent value="matrix" className="mt-6"><Matrix /></TabsContent>}
      </Tabs>
    </div>
  );
}
